import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import {
    archiveChallenge,
    deleteChallenge as apiDelete,
    generateImage,
    markReadyForReview,
    publishChallenge,
    updateChallenge,
    validateChallenge as apiValidate,
    approveCommunityChallenge,
    rejectCommunityChallenge,
} from "../lib/api";
import { QualityBadge, StatusPill, TypeBadge } from "./badges";
import Actions from "./challenge-detail/Actions";
import Fields from "./challenge-detail/Fields";
import Questions from "./challenge-detail/Questions";
import SidePanel from "./challenge-detail/SidePanel";
import ValidationPanel from "./challenge-detail/ValidationPanel";
import DetectedSignals from "./challenge-detail/DetectedSignals";
import PlayerPreview from "./challenge-detail/PlayerPreview";
import CommunityPanel from "./CommunityPanel";

function buildInitialForm(challenge) {
    return {
        title: challenge.title || "",
        shortDescription: challenge.shortDescription || "",
        descriptionHtml: challenge.descriptionHtml || "",
        rules: (challenge.rules || []).join("\n"),
        scoring: (challenge.scoring || []).join("\n"),
        hint: challenge.hint || "",
        emailPromo: challenge.emailPromo || "",
        storyContext: challenge.storyContext || "",
    };
}

export default function ChallengeDetail({ challenge, open, onOpenChange, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [imageBusy, setImageBusy] = useState(false);
    const [localImage, setLocalImage] = useState(null);
    const [validation, setValidation] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Sanitize HTML to prevent XSS — descriptionHtml comes from LLM and can be edited.
    const safeDescriptionHtml = useMemo(
        () =>
            DOMPurify.sanitize(challenge?.descriptionHtml || "", {
                ALLOWED_TAGS: ["p", "strong", "em", "ul", "ol", "li", "br", "b", "i", "span"],
                ALLOWED_ATTR: [],
            }),
        [challenge?.descriptionHtml],
    );

    const visibleSkills = useMemo(() => {
        const s = challenge?.skills || {};
        return Object.entries(s)
            .filter(([, v]) => v > 0)
            .sort(([, a], [, b]) => b - a);
    }, [challenge?.skills]);

    useEffect(() => {
        if (challenge) {
            setForm(buildInitialForm(challenge));
            setLocalImage(challenge.imageBase64 || null);
            setValidation(challenge.validation || null);
            setEditing(false);
        }
    }, [challenge]);

    if (!challenge) return null;

    const saveEdits = async () => {
        setBusy(true);
        try {
            const patch = { ...form };
            patch.rules = form.rules.split("\n").map((s) => s.trim()).filter(Boolean);
            patch.scoring = form.scoring.split("\n").map((s) => s.trim()).filter(Boolean);
            await updateChallenge(challenge.id, patch);
            toast.success("Challenge updated");
            setEditing(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const handlePublish = async () => {
        setBusy(true);
        try {
            const updated = await publishChallenge(challenge.id);
            if (updated?.validation) setValidation(updated.validation);
            toast.success("Published");
            onChanged?.();
        } catch (e) {
            const det = e.response?.data?.detail;
            if (det && typeof det === "object" && det.validation) {
                setValidation(det.validation);
                toast.error(`Publish blocked: ${det.validation.errors?.[0] || "validation failed"}`);
            } else {
                toast.error(det || "Publish failed");
            }
        } finally {
            setBusy(false);
        }
    };

    const handleValidate = async () => {
        setBusy(true);
        try {
            const v = await apiValidate(challenge.id);
            setValidation(v);
            toast.success(v.is_valid ? "Challenge is valid" : `${v.errors.length} error(s) found`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Validation failed");
        } finally {
            setBusy(false);
        }
    };

    const handleReadyForReview = async () => {
        setBusy(true);
        try {
            const updated = await markReadyForReview(challenge.id);
            setValidation(updated.validation);
            toast.success("Marked ready for review");
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Transition failed");
        } finally {
            setBusy(false);
        }
    };

    const handleArchive = async () => {
        if (!window.confirm("Archive this challenge? It will be hidden from active views.")) return;
        setBusy(true);
        try {
            await archiveChallenge(challenge.id);
            toast.success("Archived");
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Archive failed");
        } finally {
            setBusy(false);
        }
    };

    const handleApprove = async () => {
        setBusy(true);
        try {
            await approveCommunityChallenge(challenge.id);
            toast.success("Approved — moved to drafts");
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Approve failed");
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        const reason = window.prompt("Rejection reason (optional):") || "";
        setBusy(true);
        try {
            await rejectCommunityChallenge(challenge.id, reason);
            toast.success("Rejected — moved to archive");
            onOpenChange(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reject failed");
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this challenge?")) return;
        setBusy(true);
        try {
            await apiDelete(challenge.id);
            toast.success("Deleted");
            onOpenChange(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const handleGenImage = async () => {
        setImageBusy(true);
        try {
            await generateImage(challenge.id);
            toast.success("Preview image generated");
            const fresh = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/challenges/${challenge.id}`,
            ).then((r) => r.json());
            setLocalImage(fresh.imageBase64);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Image generation failed");
        } finally {
            setImageBusy(false);
        }
    };

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(challenge, null, 2));
            toast.success("JSON copied to clipboard");
        } catch {
            toast.error("Clipboard unavailable");
        }
    };

    const copyImportJson = async () => {
        try {
            const data = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/challenges/${challenge.id}/export`,
            ).then((r) => r.json());
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            toast.success("Import-format JSON copied");
        } catch {
            toast.error("Export failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl bg-[#0a0a0a] border border-white/10 text-white rounded-none p-0 max-h-[92vh] overflow-y-auto"
                data-testid="challenge-detail-dialog"
            >
                <DialogHeader className="border-b border-white/10 p-5 bg-[#121212]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <TypeBadge type={challenge.challengeType} />
                            <StatusPill status={challenge.status} />
                        </div>
                        <QualityBadge score={challenge.qualityScore} />
                    </div>
                    <DialogTitle className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight leading-none pt-2">
                        {challenge.title}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Fantasy Academy challenge detail — {challenge.challengeType} · quality score {challenge.qualityScore}.
                    </DialogDescription>
                </DialogHeader>

                <div className="p-5 md:p-6 space-y-6">
                    <Actions
                        challenge={challenge}
                        editing={editing}
                        busy={busy}
                        imageBusy={imageBusy}
                        hasImage={!!localImage}
                        validation={validation}
                        onEdit={() => setEditing(true)}
                        onSave={saveEdits}
                        onCancel={() => setEditing(false)}
                        onPublish={handlePublish}
                        onDelete={handleDelete}
                        onGenImage={handleGenImage}
                        onCopyJson={copyJson}
                        onCopyImportJson={copyImportJson}
                        onPreview={() => setPreviewOpen(true)}
                        onValidate={handleValidate}
                        onReadyForReview={handleReadyForReview}
                        onArchive={handleArchive}
                        onApprove={handleApprove}
                        onReject={handleReject}
                    />

                    <ValidationPanel validation={validation} onValidate={handleValidate} busy={busy} />

                    <DetectedSignals
                        signals={challenge.detectedSignals}
                        humanPrompt={challenge.humanPrompt}
                    />

                    {localImage && (
                        <div className="relative border border-white/10">
                            <img
                                src={`data:image/png;base64,${localImage}`}
                                alt="Challenge preview"
                                className="w-full block"
                                data-testid="challenge-image"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-2 space-y-5">
                            <Fields
                                challenge={challenge}
                                editing={editing}
                                form={form}
                                setForm={setForm}
                                safeDescriptionHtml={safeDescriptionHtml}
                            />
                            <Questions questions={challenge.questions} />
                        </div>

                        <SidePanel challenge={challenge} visibleSkills={visibleSkills} />
                    </div>

                    <CommunityPanel challengeId={challenge.id} />
                </div>
            </DialogContent>
            <PlayerPreview
                challenge={challenge}
                open={previewOpen}
                onOpenChange={setPreviewOpen}
            />
        </Dialog>
    );
}
